import React, { useState } from 'react';
import moment from 'moment-timezone';

import { DangerAlert } from '@magoosh/lib/alerts';
import { DangerBadge, DefaultBadge, SuccessBadge } from '@magoosh/lib/badge';
import { DangerButton, DefaultButton, DefaultFilledButton } from '@magoosh/lib/button';
import { useModalContext } from '@magoosh/context/modal_context';
import { PaymentDetails } from 'types/payment_details';
import { OutstandingPayment, Payment, PurchaseAgreement, Refund } from 'types/purchase_agreement';
import { paths } from 'config/path_helpers';
import { formatPrice } from 'utilities/money';

import CancelPlan from '../cancel_plan';
import EditPaymentMethod from '../edit_payment_method';
import PausePlan from '../pause_plan';
import PaymentMethodSummary from '../payment_method_summary';
import UnpausePlan from '../unpause_plan';

import style from './style.module.scss';

interface Props {
  onSuccess: () => void;
  paymentMethod: PaymentDetails | null;
  purchaseAgreement: PurchaseAgreement;
  stripeToken: string;
}

const PAYMENT_BADGES = {
  failed: DangerBadge,
  incomplete: DangerBadge,
  scheduled: DefaultBadge,
  successful: SuccessBadge
};

const STATUS_BADGES = {
  activated: SuccessBadge,
  cancelled: DangerBadge,
  expired: DangerBadge,
  // eslint-disable-next-line @typescript-eslint/camelcase
  not_activated: DefaultBadge,
  suspended: DangerBadge,
  upgraded: DefaultBadge
};

const formatDate = (timestamp: number) => moment.unix(timestamp).format('MMMM D, YYYY');

const paymentDate = (payment: Payment) => payment.paidAt || payment.dueAt;

const startDate = (purchaseAgreement: PurchaseAgreement) => {
  if (!purchaseAgreement.activatedAt) return null;

  const starts = moment.unix(purchaseAgreement.activatedAt).isAfter(moment());

  return `${starts ? 'Starts' : 'Started'} on ${formatDate(purchaseAgreement.activatedAt)}`;
};

const describeStartDate = (purchaseAgreement: PurchaseAgreement) => {
  const started = startDate(purchaseAgreement);

  return started && `${started}.`;
};

// How often a recurring plan comes around again, from the length of the period it grants
const describeRenewalCadence = (purchaseAgreement: PurchaseAgreement) => {
  const { durationDays, durationMonths, durationYears } = purchaseAgreement.subscriptionPlan;

  if (durationYears === 1) return 'annually';
  if (durationYears) return `every ${durationYears} years`;
  if (durationMonths === 1) return 'monthly';
  if (durationMonths === 3) return 'quarterly';
  if (durationMonths) return `every ${durationMonths} months`;
  if (durationDays === 7) return 'weekly';
  if (durationDays) return `every ${durationDays} days`;

  return null;
};

// A plan that is still renewing says so instead of naming an expiry it will never reach
const describeRenewal = (purchaseAgreement: PurchaseAgreement) => {
  if (purchaseAgreement.status !== 'activated' || !purchaseAgreement.subscriptionPlan.recurring) return null;

  const started = startDate(purchaseAgreement);
  const cadence = describeRenewalCadence(purchaseAgreement);
  if (!started || !cadence) return null;

  return `${started} and renews ${cadence}`;
};

const describeEndDate = (purchaseAgreement: PurchaseAgreement) => {
  if (purchaseAgreement.status === 'upgraded') return 'Upgraded to another plan.';
  if (!purchaseAgreement.expiresAt) return null;

  const expiresAt = moment.unix(purchaseAgreement.expiresAt);
  const expired = expiresAt.isBefore(moment());

  return `${expired ? 'Expired' : 'Expires'} on ${formatDate(purchaseAgreement.expiresAt)} (${expiresAt.fromNow()}).`;
};

// Everything a plan owes, told as one: what it adds up to and when it fell due. A legacy
// subscription records no amount for what it is owed, so its payments go unpriced.
const describeOutstandingPayments = (payments: OutstandingPayment[]) => {
  const plural = payments.length > 1;
  const totals = payments.map((payment) => payment.total);
  const dueDates = payments.map((payment) => payment.dueAt).filter(Boolean).sort();

  let sentence = `Your ${plural ? 'payments' : 'payment'}`;

  if (totals.every((total) => total !== null)) {
    sentence += ` of ${formatPrice(totals.reduce((sum, total) => sum + total, 0))}`;
  }

  if (dueDates.length > 0) {
    const first = formatDate(dueDates[0]);
    const last = formatDate(dueDates[dueDates.length - 1]);

    sentence += first === last ? ` due ${first}` : ` due between ${first} and ${last}`;
  }

  sentence += " didn't go through.";

  return sentence;
};

// A payment waiting on the student's bank to confirm it hasn't been declined: nothing is wrong with
// the card, the bank just wants the student to authorize the charge
const describeAwaitingAuthorization = (payments: OutstandingPayment[]) => (
  `Your bank needs you to confirm ${payments.length > 1 ? 'these payments' : 'this payment'} ` +
    'before it can go through.'
);

const Plan: React.FC<Props> = (props) => {
  const { purchaseAgreement } = props;

  const { setModal } = useModalContext();

  // A recurring plan keeps collecting, so its history stays out of the way until asked for
  const [showPastPayments, setShowPastPayments] = useState(!purchaseAgreement.subscriptionPlan.recurring);

  const StatusBadge = STATUS_BADGES[purchaseAgreement.status] || DefaultBadge;

  const presentEditPaymentMethodModal = () => {
    setModal({
      header: 'Payment Method',
      body: (
        <EditPaymentMethod
          onClose={() => setModal(null)}
          onSuccess={() => {
            props.onSuccess();
            setModal(null);
          }}
          paymentMethod={props.paymentMethod}
          purchaseAgreement={purchaseAgreement}
          stripeToken={props.stripeToken}
        />
      )
    });
  };

  const presentPausePlanModal = () => {
    setModal({
      header: 'Pause Plan',
      body: (
        <PausePlan
          onClose={() => setModal(null)}
          onSuccess={() => {
            props.onSuccess();
            setModal(null);
          }}
          purchaseAgreement={purchaseAgreement}
        />
      )
    });
  };

  const presentUnpausePlanModal = () => {
    setModal({
      header: 'Unpause Plan',
      body: (
        <UnpausePlan
          onClose={() => setModal(null)}
          onSuccess={() => {
            props.onSuccess();
            setModal(null);
          }}
          purchaseAgreement={purchaseAgreement}
        />
      )
    });
  };

  const presentCancelPlanModal = () => {
    setModal({
      header: 'Cancel Plan',
      body: (
        <CancelPlan
          onClose={() => setModal(null)}
          onSuccess={() => {
            props.onSuccess();
            setModal(null);
          }}
          purchaseAgreement={purchaseAgreement}
          expiresOn={purchaseAgreement.expiresAt && formatDate(purchaseAgreement.expiresAt)}
        />
      )
    });
  };

  // A payment we hold is asked for by its id; one rebuilt from a legacy subscription has none, so
  // it is asked for by the subscription it came from and the processor's own reference
  // A payment rebuilt from a legacy subscription has no id, so the processor's own reference names
  // it instead. One with neither - a purchase entered by hand, or paid by check - has no receipt.
  const receiptPath = (payment: Payment) => {
    if (payment.id) {
      return paths.purchaseAgreementPaymentReceipt(purchaseAgreement.id, payment.id);
    } else if (payment.identifier) {
      return paths.subscriptionPaymentReceipt(
        purchaseAgreement.legacySubscriptionId,
        payment.identifier
      );
    } else {
      return null;
    }
  };

  // What the student owes is settled invoice by invoice on Stripe's own page, the oldest first:
  // paid there if it failed, or authorized there if their bank wants the charge confirmed
  const nextOutstandingPayment = purchaseAgreement.outstandingPayments.find((payment) => payment.invoiceUrl);
  const awaitingAuthorization = purchaseAgreement.outstandingPayments.filter(
    (payment) => payment.status === 'incomplete'
  );

  const outstandingPaymentsAction = () => {
    if (!nextOutstandingPayment) return null;

    return (
      <DangerButton href={nextOutstandingPayment.invoiceUrl} size="sm">
        {nextOutstandingPayment.status === 'incomplete' ? 'Authorize Payment' : 'Pay Invoice'}
      </DangerButton>
    );
  };

  const paymentRow = (payment: Payment, idx: number) => {
    const PaymentBadge = PAYMENT_BADGES[payment.status] || DefaultBadge;

    return (
      <tr key={payment.id || `upcoming-${idx}`}>
        <td>{formatDate(paymentDate(payment))}</td>

        <td className="text-right">{formatPrice(payment.total)}</td>

        <td><PaymentBadge>{payment.status.toUpperCase()}</PaymentBadge></td>

        <td className="text-right">
          {
            payment.status === 'successful' && receiptPath(payment) && (
              <a className="small text-gray-light" href={receiptPath(payment)}>
                Receipt
              </a>
            )
          }
        </td>
      </tr>
    );
  };

  // Refunds read as part of the payment history, each on the date the money went back
  const refundRow = (refund: Refund, idx: number) => (
    <tr key={`refund-${idx}`}>
      <td>{formatDate(refund.refundedAt)}</td>

      <td className="text-right">{formatPrice(-refund.total)}</td>

      <td><DefaultBadge>REFUNDED</DefaultBadge></td>

      <td />
    </tr>
  );

  const historyRows = [
    ...purchaseAgreement.payments.map((payment: Payment, idx: number) => (
      { date: paymentDate(payment), row: paymentRow(payment, idx) }
    )),
    ...purchaseAgreement.refunds.map((refund: Refund, idx: number) => (
      { date: refund.refundedAt, row: refundRow(refund, idx) }
    ))
  ].sort((a, b) => (a.date || 0) - (b.date || 0)).map(({ row }) => row);

  const paymentsTable = (
    <table className={`table u-margin-A-n ${style.paymentsTable}`}>
      <tbody>
        {historyRows}
      </tbody>
    </table>
  );

  return (
    <div className="d-flex flex-column gap-m">
      <div className="align-items-center d-flex gap-s">
        <div className="align-items-center d-flex flex-1 gap-xs">
          <h4 className="u-margin-A-n"><strong>{purchaseAgreement.displayName}</strong></h4>

          {
            (purchaseAgreement.canChangePlan || purchaseAgreement.canBeCancelled) && (
              <div className="small text-gray-light">
                (
                {
                  purchaseAgreement.canChangePlan && (
                    <a href={`${paths.plans()}#offers`}>Change Plan</a>
                  )
                }
                {
                  purchaseAgreement.canChangePlan && purchaseAgreement.canBeCancelled && (
                    <span aria-hidden="true" style={{ margin: '0 6px' }}>|</span>
                  )
                }
                {
                  purchaseAgreement.canBeCancelled && (
                    <a onClick={presentCancelPlanModal} style={{ cursor: 'pointer' }}>Cancel</a>
                  )
                }
                )
              </div>
            )
          }
        </div>

        <StatusBadge>{purchaseAgreement.status.replace('_', ' ').toUpperCase()}</StatusBadge>
      </div>

      {
        purchaseAgreement.outstandingPayments.length > 0 && (
          <DangerAlert className="d-flex flex-column gap-s">
            <div>
              {
                awaitingAuthorization.length === purchaseAgreement.outstandingPayments.length ? (
                  <div>{describeAwaitingAuthorization(awaitingAuthorization)}</div>
                ) : (
                  <div>{describeOutstandingPayments(purchaseAgreement.outstandingPayments)}</div>
                )
              }
            </div>

            <div>{outstandingPaymentsAction()}</div>
          </DangerAlert>
        )
      }

      <div>
        {
          describeRenewal(purchaseAgreement) ? (
            <div>{describeRenewal(purchaseAgreement)}</div>
          ) : (
            <>
              <div>{describeStartDate(purchaseAgreement)}</div>
              <div>{describeEndDate(purchaseAgreement)}</div>
            </>
          )
        }
      </div>

      {
        purchaseAgreement.status === 'paused' && (
          <div className="d-flex flex-column gap-s">
            <div>This plan is paused.</div>

            {
              purchaseAgreement.canBeResumed ? (
                <div>
                  <DefaultButton onClick={presentUnpausePlanModal} outline size="sm">
                    Unpause Plan
                  </DefaultButton>
                </div>
              ) : (
                // Outstanding payments are the only thing that holds a paused plan shut
                <div className="small text-gray-light">
                  This plan has payments outstanding. Please resolve them before unpausing it.
                </div>
              )
            }
          </div>
        )
      }

      {
        purchaseAgreement.canBePaused && (
          <div>
            <DefaultButton onClick={presentPausePlanModal} outline size="sm">
              Pause Plan
            </DefaultButton>
          </div>
        )
      }

      {
        // A plan can only be paused once a month, so one unpaused a short while ago keeps its
        // button while the wait runs, with the date it can be paused again
        !purchaseAgreement.canBePaused && !!purchaseAgreement.pausableAgainAt && (
          <div className="d-flex flex-column gap-s">
            <div>
              <DefaultFilledButton disabled outline size="sm">
                Pause Plan
              </DefaultFilledButton>
            </div>

            <div className="small text-gray-light">
              After resuming a paused plan, you must wait a month before pausing again. You can pause this plan again on{' '}
              {formatDate(purchaseAgreement.pausableAgainAt)}.
            </div>
          </div>
        )
      }

      {
        purchaseAgreement.billsAgain && (
          <div className="d-flex flex-column gap-s">
            <div className="align-items-center d-flex gap-xs">
              <h5 className="u-margin-A-n"><strong>Payment Method</strong></h5>

              <div className="small text-gray-light">
                (
                <a onClick={presentEditPaymentMethodModal} style={{ cursor: 'pointer' }}>Edit</a>
                )
              </div>
            </div>

            <PaymentMethodSummary paymentMethod={props.paymentMethod} />
          </div>
        )
      }

      {
        purchaseAgreement.nextRecurringPayment && (
          <div className="d-flex flex-column gap-s">
            <h5 className="u-margin-A-n"><strong>Upcoming Payment</strong></h5>

            <div className="align-items-center d-flex gap-s">
              <div className="flex-1">{formatDate(purchaseAgreement.nextRecurringPayment.dueAt)}</div>

              <div>{formatPrice(purchaseAgreement.nextRecurringPayment.total)}</div>
            </div>
          </div>
        )
      }

      {
        (purchaseAgreement.payments.length > 0 || purchaseAgreement.refunds.length > 0) && (
          showPastPayments ? (
            <div className="d-flex flex-column gap-s">
              <div>
                <h5 className="u-margin-A-n"><strong>Payments</strong></h5>

                {
                  purchaseAgreement.paymentMethod.installments && (
                    <small className="text-muted">Purchased on an installment plan</small>
                  )
                }
              </div>

              {paymentsTable}
            </div>
          ) : (
            <div
              className="small text-gray-light"
              onClick={() => setShowPastPayments(true)}
              style={{ cursor: 'pointer' }}
            >
              Show past payments
            </div>
          )
        )
      }
    </div>
  );
};

export default Plan;
